import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { StaffRole } from '../types';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Helper to get the simulated app date (January 25, 2026)
  const getSimulatedDate = () => new Date('2026-01-25T08:00:00');
  const simulatedDate = getSimulatedDate();

  const navItems = [
    { path: '/', icon: 'fa-chart-pie', label: 'Dashboard' },
    { path: '/new', icon: 'fa-file-signature', label: 'Start Intake' },
    { path: '/settings', icon: 'fa-sliders', label: 'Configuration' }
  ];

  if (user?.role === StaffRole.ADMIN || user?.role === StaffRole.SUPER_ADMIN) {
    navItems.splice(2, 0, { path: '/admin', icon: 'fa-user-shield', label: 'Admin Panel' });
  }

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden font-sans text-slate-900 dark:text-slate-100 transition-colors duration-300">
      {/* Sidebar */}
      <aside className="w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex-shrink-0 flex flex-col relative z-20 shadow-[4px_0_24px_rgba(0,0,0,0.02)] transition-colors duration-300">
        <div className="p-8 pb-6 flex flex-col items-center justify-center text-center min-h-[160px]">
          <span className="text-3xl font-serif font-bold text-amber-600 dark:text-amber-500 leading-tight">Lluis Law</span>
          <div className="flex items-center gap-2 mt-2">
            <div className="h-px w-3 bg-slate-100 dark:bg-slate-800"></div>
            <span className="text-[9px] font-sans font-black uppercase tracking-[0.3em] text-slate-400 dark:text-slate-500">Fact Gather</span>
            <div className="h-px w-3 bg-slate-100 dark:bg-slate-800"></div>
          </div>
        </div>
        
        <div className="px-6 mb-6">
          <div className="h-px w-full bg-slate-100 dark:bg-slate-800"></div>
        </div>
        
        <nav className="flex-1 px-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-lg shadow-slate-200 dark:shadow-none' 
                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                <i className={`fas ${item.icon} w-5 text-center ${isActive ? (theme === 'dark' ? 'text-amber-600' : 'text-amber-500') : 'group-hover:text-amber-600 transition-colors'}`}></i>
                <span className={`text-sm font-semibold tracking-tight ${isActive ? '' : 'font-medium'}`}>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Profile Section - Simple non-clickable display */}
        <div className="p-6 mt-auto space-y-4">
          <div className="w-full bg-slate-50 dark:bg-slate-950/50 rounded-2xl p-4 border border-slate-100 dark:border-slate-800 flex items-center gap-4">
            <div className="w-10 h-10 flex-shrink-0 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center text-slate-700 dark:text-slate-300 shadow-sm border border-white dark:border-slate-700">
              <span className="font-serif font-bold italic">{user?.name.charAt(0) || 'S'}</span>
            </div>
            <div className="overflow-hidden flex-1">
              <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate">{user?.name || 'Staff Member'}</p>
              <p className="text-[10px] text-amber-700 dark:text-amber-500 font-bold uppercase tracking-widest truncate">{user?.role || 'Authorized'}</p>
            </div>
          </div>
          
          <button 
            type="button"
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 py-3 text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl transition-all"
          >
            <i className="fas fa-sign-out-alt"></i> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto relative bg-slate-50/50 dark:bg-slate-950 transition-colors">
        <header className="sticky top-0 z-10 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-b border-slate-200/60 dark:border-slate-800 px-8 py-5 flex items-center justify-between transition-colors">
          <h2 className="text-2xl font-serif font-bold text-slate-900 dark:text-slate-100">
            {navItems.find(item => item.path === location.pathname)?.label || 'Case Management'}
          </h2>
          <div className="flex items-center gap-4 sm:gap-6">
            <button 
              type="button"
              onClick={toggleTheme}
              className="p-2.5 bg-slate-50 dark:bg-slate-800 text-slate-400 dark:text-slate-300 hover:text-amber-700 dark:hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10 rounded-full transition-all border border-slate-100 dark:border-slate-700"
              title={`Switch to ${theme === 'light' ? 'Dark' : 'Light'} Mode`}
            >
              <i className={`fas ${theme === 'light' ? 'fa-moon' : 'fa-sun'} text-lg`}></i>
            </button>
            <button type="button" className="p-2.5 bg-slate-50 dark:bg-slate-800 text-slate-400 dark:text-slate-300 hover:text-amber-700 dark:hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10 rounded-full transition-all relative border border-slate-100 dark:border-slate-700">
              <i className="fas fa-bell text-lg"></i>
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white dark:ring-slate-900"></span>
            </button>
            <div className="h-8 w-px bg-slate-200 dark:bg-slate-800"></div>
            <div className="text-right hidden sm:block">
              <div className="text-sm font-bold text-slate-800 dark:text-slate-200">
                {simulatedDate.toLocaleDateString('en-US', { weekday: 'long' })}
              </div>
              <div className="text-[10px] text-slate-400 dark:text-slate-500 uppercase font-bold tracking-widest">
                 {simulatedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          </div>
        </header>

        <div className="p-8 max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-2 duration-700">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;